import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, Save, CheckCircle, ChevronLeft, ChevronRight, AlertTriangle, RotateCcw, XCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { MiniInventoryModal } from '@/components/inventory/MiniInventoryModal';
import { inventoryAuditService } from '@/services/inventoryAuditService';
import type { InventoryAudit, InventoryAuditItem } from '@/types/inventory-audit.types';
import { InventoryAuditItemsTable } from './components/InventoryAuditItemsTable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export const InventoryAuditDetailPage: React.FC = () => {
  const { auditId } = useParams<{ auditId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [audit, setAudit] = useState<InventoryAudit | null>(null);
  const [editableItems, setEditableItems] = useState<InventoryAuditItem[]>([]);
  const [originalReviewStatus, setOriginalReviewStatus] = useState<{[key: number]: boolean}>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [changedItemIds, setChangedItemIds] = useState<Set<string>>(new Set());
  const [showOnlyDiscrepancies, setShowOnlyDiscrepancies] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isSaving, setIsSaving] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all'); // 'all', 'reviewed', 'pending'
  const [showMiniInventory, setShowMiniInventory] = useState(false);
  const [showRecountModal, setShowRecountModal] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [showReviewConfirmModal, setShowReviewConfirmModal] = useState(false);
  const [showReviewSuccessModal, setShowReviewSuccessModal] = useState(false);
  const [showFinalizeConfirmModal, setShowFinalizeConfirmModal] = useState(false);
  const [showFinalizeSuccessModal, setShowFinalizeSuccessModal] = useState(false);
  const [showFinalizeErrorModal, setShowFinalizeErrorModal] = useState(false);
  const [showRecountSuccessModal, setShowRecountSuccessModal] = useState(false);
  const [recountedItemsCount, setRecountedItemsCount] = useState(0);
  const [finalizeSuccessMessage, setFinalizeSuccessMessage] = useState('');
  const [itemsToReview, setItemsToReview] = useState(0);
  const [reviewedItemsCount, setReviewedItemsCount] = useState(0);
  const [incompleteItemsCount, setIncompleteItemsCount] = useState(0);

  // States for message modals to replace alerts
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModal, setMessageModal] = useState({
    type: 'info' as 'success' | 'error' | 'warning' | 'info',
    title: '',
    message: ''
  });
  const [showNoItemsToReviewModal, setShowNoItemsToReviewModal] = useState(false);
  const [showNoItemsToRecountModal, setShowNoItemsToRecountModal] = useState(false);
  const [showRecountErrorModal, setShowRecountErrorModal] = useState(false);
  const [showSaveErrorModal, setShowSaveErrorModal] = useState(false);
  const [showMiniCompleteModal, setShowMiniCompleteModal] = useState(false);
  const [showMiniCancelModal, setShowMiniCancelModal] = useState(false);
  const [showIncompleteItemsModal, setShowIncompleteItemsModal] = useState(false);
  const [showFinalSuccessModal, setShowFinalSuccessModal] = useState(false);
  const [finalSuccessData, setFinalSuccessData] = useState({
    totalItems: 0,
    discrepancies: 0,
    totalValue: 0
  });
  const [showDiscrepancyConfirmModal, setShowDiscrepancyConfirmModal] = useState(false);
  const [discrepancyModalData, setDiscrepancyModalData] = useState({
    discrepancies: [] as InventoryAuditItem[],
    count: 0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estado para verificar reconteo
  const [recountStatus, setRecountStatus] = useState<{
    hasRecount: boolean;
    recountAuditId: number | null;
    recountStatus?: string;
  }>({ hasRecount: false, recountAuditId: null });

  useEffect(() => {
    const loadAuditData = async () => {
      if (!auditId) return;

      setLoading(true);
      setError(null);

      try {
        // Try to load audit from API
        const auditResponse = await inventoryAuditService.getAuditById(auditId);

        if (auditResponse.success && auditResponse.data) {
          setAudit(auditResponse.data);

          // Load audit items
          const itemsResponse = await inventoryAuditService.getAuditItems(auditId);
          if (itemsResponse.success && itemsResponse.data) {
            setEditableItems([...itemsResponse.data]);
            // Guardar los valores originales de isReviewed
            const originalStatus: {[key: number]: boolean} = {};
            itemsResponse.data.forEach((item: InventoryAuditItem) => {
              originalStatus[item.id] = item.isReviewed;
            });
            setOriginalReviewStatus(originalStatus);
          } else {
            setEditableItems([]);
            setOriginalReviewStatus({});
          }
        } else {
          // If not found in API, check localStorage for dynamically created inventory
          const storedData = localStorage.getItem(`inventory-${auditId}`);
          let inventoryData = null;

          if (storedData) {
            try {
              inventoryData = JSON.parse(storedData);
            } catch (e) {
              console.error('Error parsing stored inventory data:', e);
            }
          }

          const foundAudit = {
            id: auditId,
            locationId: inventoryData?.locationId || 2,
            locationName: inventoryData?.locationName || 'Sucursal Norte',
            auditDate: inventoryData?.auditDate || new Date().toISOString().split('T')[0],
            auditorId: 1,
            auditorName: inventoryData?.auditorName || 'Admin Usuario',
            status: 'in_progress' as const,
            totalItems: 0,
            itemsAudited: 0,
            discrepancies: 0,
            totalValue: 0,
            notes: inventoryData?.notes || 'Inventario creado dinámicamente',
            hasBeenRecounted: false,
            createdAt: new Date().toISOString(),
            completedAt: null,
            reviewedAt: null
          };

          setAudit(foundAudit);

          // Para inventarios nuevos creados localmente, crear items de ejemplo
          const items = [
            {
              id: `${auditId}-item-1`,
              auditId: auditId,
              ingredientId: 1,
              ingredientName: 'Palta',
              category: 'Verduras',
              expectedQuantity: 50,
              actualQuantity: 0,
              confirmedQuantity: 0,
              discrepancy: 0,
              unit: 'unidades',
              unitCost: 3.5,
              totalValue: 0,
              isReviewed: false,
              isRecount: false
            },
            {
              id: `${auditId}-item-2`,
              auditId: auditId,
              ingredientId: 2,
              ingredientName: 'Tomate',
              category: 'Verduras',
              expectedQuantity: 80,
              actualQuantity: 0,
              confirmedQuantity: 0,
              discrepancy: 0,
              unit: 'kg',
              unitCost: 2.8,
              totalValue: 0,
              isReviewed: false,
              isRecount: false
            },
            {
              id: `${auditId}-item-3`,
              auditId: auditId,
              ingredientId: 3,
              ingredientName: 'Pan',
              category: 'Panadería',
              expectedQuantity: 40,
              actualQuantity: 0,
              confirmedQuantity: 0,
              discrepancy: 0,
              unit: 'unidades',
              unitCost: 0.8,
              totalValue: 0,
              isReviewed: false,
              isRecount: false
            }
          ];

          setEditableItems([...items]);
          setOriginalReviewStatus({});
        }
      } catch (error) {
        console.error('Error loading audit data:', error);
        setError('Error al cargar el inventario');
      } finally {
        setLoading(false);
      }
    };

    loadAuditData();
  }, [auditId]);

  // Función para verificar estado de reconteo
  const checkRecountStatus = async () => {
    if (!auditId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/inventory-audits/${auditId}/recount-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRecountStatus({
            hasRecount: data.hasRecount,
            recountAuditId: data.recountAuditId,
            recountStatus: data.recountStatus
          });
        }
      }
    } catch (error) {
      console.error('Error al verificar estado de reconteo:', error);
    }
  };

  // Effect para verificar estado de reconteo al cargar
  useEffect(() => {
    if (auditId) {
      checkRecountStatus();
    }
  }, [auditId]);

  // Mostrar modal automáticamente cuando se carga un inventario en proceso y el usuario es CAJERO (positionId = 3)
  useEffect(() => {
    if (audit && audit.status === 'in_progress' && user?.positionId === 3) {
      setShowMiniInventory(true);
    }
  }, [audit, user]);

  const markItemChanged = (itemId: string) => {
    setChangedItemIds(prev => new Set(prev).add(itemId));
  };

  const updateActualQuantity = (itemId: string, newActualQuantity: number) => {
    setEditableItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = {
          ...item,
          actualQuantity: newActualQuantity,
          discrepancy: newActualQuantity - item.expectedQuantity,
          totalValue: newActualQuantity * item.unitCost
        };
        return updatedItem;
      }
      return item;
    }));
    markItemChanged(itemId);
    setHasChanges(true);
  };

  const updateConfirmedQuantity = (itemId: string, numericValue: number) => {
    setEditableItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          confirmedQuantity: numericValue,
          isReviewed: item.isReviewed
        };
      }
      return item;
    }));
    markItemChanged(itemId);
    setHasChanges(true);
  };

  const updateItemReviewStatus = (itemId: string, isReviewed: boolean) => {
    setEditableItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          isReviewed
        };
      }
      return item;
    }));
    markItemChanged(itemId);
    setHasChanges(true);
  };

  const updateDiscrepancyReason = (itemId: string, reason: string) => {
    setEditableItems(prev => prev.map(item => {
      if (item.id === itemId) {
        markItemChanged(itemId);
        return {
          ...item,
          discrepancyReason: reason
        };
      }
      return item;
    }));
    setHasChanges(true);
  };

  const saveChanges = async (showAlert: boolean = true) => {
    await performSave(showAlert);
  };

  const startReview = () => {
    if (!audit) return;

    if (audit.status === 'completed') {
      // Contar solo items que están marcados como revisados pero NO estaban revisados previamente
      const itemsToReviewList = editableItems.filter(
        item => item.isReviewed && !originalReviewStatus[item.id]
      );

      if (itemsToReviewList.length === 0) {
        setShowNoItemsToReviewModal(true);
        return;
      }

      setItemsToReview(itemsToReviewList.length);
      setShowReviewConfirmModal(true);
    } else {
      // Lógica original para otros estados
      performSave(true);
    }
  };

  const confirmReview = async () => {
    setShowReviewConfirmModal(false);
    await performReview();
  };

  const performReview = async () => {
    if (!audit) return;

    setIsSaving(true);
    try {
      // Solo enviar items que están siendo revisados por primera vez
      const itemsToReview = editableItems.filter(
        item => item.isReviewed && !originalReviewStatus[item.id]
      );

      const updates = itemsToReview.map(item => ({
        itemId: item.id,
        data: {
          actualQuantity: item.actualQuantity,
          confirmedQuantity: item.confirmedQuantity !== undefined && item.confirmedQuantity !== null ? item.confirmedQuantity : item.actualQuantity,
          discrepancyReason: item.discrepancyReason || '',
          isReviewed: true
        }
      }));

      console.log('Reviewing items:', updates);

      // Use the bulk update API only for reviewed items
      const response = await inventoryAuditService.bulkUpdateItems(audit.id.toString(), updates);

      if (response.success) {
        setHasChanges(false);

        // Update originalReviewStatus for the newly reviewed items
        setOriginalReviewStatus(prev => {
          const updated = { ...prev };
          itemsToReview.forEach(item => {
            updated[item.id] = true;
          });
          return updated;
        });

        // Show success message
        setShowSaveSuccessModal(true);
      } else {
        throw new Error(response.error || 'Error al realizar la revisión');
      }
    } catch (error) {
      console.error('Error reviewing items:', error);
      setShowSaveErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmFinalizeInventory = async () => {
    setShowFinalizeConfirmModal(false);
    await finalizeInventoryReview();
  };

  const finalizeInventoryReview = async () => {
    if (!audit) return;

    try {
      setIsSaving(true);

      // Primero guardar la revisión (cantidades confirmadas, checkboxes, motivos)
      await performSave(false);

      // Luego cambiar el estado a 'reviewed'
      const response = await inventoryAuditService.updateAudit(audit.id.toString(), {
        status: 'reviewed'
      });

      if (response.success && response.data) {
        // Actualizar el estado local con la respuesta del API
        setAudit(response.data);

        // Verificar si hay items no revisados con discrepancias
        const unreviewedItemsWithDiscrepancies = editableItems.filter(
          item => !item.isReviewed && item.discrepancy !== 0
        );

        // Configurar mensaje condicional
        let successMessage = 'Inventario finalizado exitosamente.';
        if (unreviewedItemsWithDiscrepancies.length > 0) {
          successMessage += ' El inventario está listo para reconteo de ser necesario.';
        }

        setFinalizeSuccessMessage(successMessage);
        setShowFinalizeSuccessModal(true);
      } else {
        throw new Error(response.error || 'Error al finalizar el inventario');
      }
    } catch (error) {
      console.error('Error finalizing inventory review:', error);
      setShowFinalizeErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendToRecount = () => {
    setShowRecountModal(true);
  };

  const confirmRecount = async () => {
    if (!audit) return;

    try {
      // Filtrar items que necesitan reconteo (con discrepancias no revisadas)
      const itemsToRecount = editableItems.filter(
        item => item.discrepancy !== 0 && !item.isReviewed
      );

      if (itemsToRecount.length === 0) {
        setShowNoItemsToRecountModal(true);
        return;
      }

      // Crear nuevo inventario de reconteo usando el API
      const recountData = {
        originalAuditId: audit.id.toString(),
        locationId: audit.locationId.toString(),
        auditDate: new Date().toISOString().split('T')[0],
        notes: `Reconteo del inventario ${audit.id} - ${itemsToRecount.length} productos con discrepancias`,
        isRecount: true,
        items: itemsToRecount.map(item => ({
          ingredientId: item.ingredientId.toString(),
          expectedQuantity: item.actualQuantity // La cantidad actual se convierte en la esperada para el reconteo
        }))
      };

      console.log('Creating recount audit:', recountData);

      const response = await inventoryAuditService.createRecountAudit(recountData);

      if (response.success && response.data) {
        setShowRecountModal(false);
        setHasChanges(false);

        // Actualizar estado para mostrar que ya tiene reconteo
        setRecountStatus({
          hasRecount: true,
          recountAuditId: parseInt(response.data.newAuditId),
          recountStatus: 'in_progress'
        });

        // Mostrar mensaje de éxito
        setRecountedItemsCount(response.data.itemsToRecount);
        setShowRecountSuccessModal(true);
      } else {
        throw new Error(response.error || 'Error al crear el inventario de reconteo');
      }
    } catch (error) {
      console.error('Error creating recount audit:', error);
      setShowRecountErrorModal(true);
    }
  };

  const cancelRecount = () => {
    setShowRecountModal(false);
  };

  const performSave = async (showAlert: boolean = true) => {
    if (!audit) return;

    setIsSaving(true);
    try {
      // Solo enviar items que fueron modificados
      const updates = editableItems
        .filter(item => changedItemIds.has(item.id))
        .map(item => ({
          itemId: item.id,
          data: {
            actualQuantity: item.actualQuantity,
            confirmedQuantity: item.confirmedQuantity !== undefined && item.confirmedQuantity !== null ? item.confirmedQuantity : item.actualQuantity,
            discrepancyReason: item.discrepancyReason || '',
            isReviewed: item.isReviewed
          }
        }));

      if (updates.length === 0) {
        if (showAlert) setShowSaveSuccessModal(true);
        return;
      }

      console.log(`Saving ${updates.length} changed items to API`);

      // Use the bulk update API
      const response = await inventoryAuditService.bulkUpdateItems(audit.id.toString(), updates);

      if (response.success) {
        setHasChanges(false);
        setChangedItemIds(new Set());

        // Show success message only if requested
        if (showAlert) {
          setShowSaveSuccessModal(true);
        }
      } else {
        throw new Error(response.error || 'Error al guardar los conteos');
      }
    } catch (error) {
      console.error('Error saving audit items:', error);
      setShowSaveErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMiniInventoryComplete = async () => {
    setShowMiniInventory(false);
    setShowMiniCompleteModal(true);
  };

  const handleMiniInventoryCancel = () => {
    setShowMiniInventory(false);
    setShowMiniCancelModal(true);
  };

  const completeInventory = () => {
    // Un item está incompleto si no fue contado: actualQuantity es null (nunca guardado) Y no fue tocado en esta sesión
    const incompleteItems = editableItems.filter(item =>
      (item.actualQuantity === null || item.actualQuantity === undefined) && !changedItemIds.has(item.id)
    );

    if (incompleteItems.length > 0) {
      setIncompleteItemsCount(incompleteItems.length);
      setShowIncompleteItemsModal(true);
      return;
    }

    const discrepancies = editableItems.filter(item => item.discrepancy !== 0);

    if (discrepancies.length > 0) {
      setDiscrepancyModalData({
        discrepancies: discrepancies,
        count: discrepancies.length
      });
      setShowDiscrepancyConfirmModal(true);
      return;
    }

    // If no discrepancies, proceed directly
    finalizeInventoryWithDiscrepancies([]);
  };

  const finalizeInventoryWithDiscrepancies = (discrepancies: InventoryAuditItem[]) => {
    // When there are discrepancies, just update the audit status to completed
    // without showing a success modal since this should be handled differently
    if (audit) {
      setAudit(prevAudit => ({
        ...prevAudit,
        status: 'completed'
      }));
    }

    // For cases without discrepancies, show the success modal
    if (discrepancies.length === 0) {
      const totalValue = editableItems.reduce((sum, item) => sum + (item.unitCost * (item.actualQuantity ?? 0)), 0);
      setFinalSuccessData({
        totalItems: editableItems.length,
        discrepancies: 0,
        totalValue: totalValue
      });
      setShowFinalSuccessModal(true);
    }
  };

  const confirmDiscrepanciesAndFinalize = async () => {
    setShowDiscrepancyConfirmModal(false);

    try {
      // 1. Save the count first
      await performSave(false); // Don't show alert

      // 2. Change inventory status to completed
      if (audit) {
        await inventoryAuditService.updateAudit(audit.id.toString(), {
          status: 'completed',
          totalItems: editableItems.length,
          itemsAudited: editableItems.filter(item => item.actualQuantity !== null && item.actualQuantity !== undefined).length,
          discrepancies: discrepancyModalData.count,
          totalValue: editableItems.reduce((sum, item) => sum + (item.unitCost * (item.actualQuantity ?? 0)), 0)
        });
      }

      // 3. Redirect to main screen
      navigate('/inventory');

      // 4. Show finalized modal (this will be handled by the main inventory page)
      // We'll pass a flag or use localStorage to show the modal there
      localStorage.setItem('inventoryFinalized', JSON.stringify({
        success: true,
        totalItems: editableItems.length,
        discrepancies: discrepancyModalData.count,
        timestamp: new Date().toISOString()
      }));

    } catch (error) {
      console.error('Error finalizing inventory:', error);
      // Show error modal if something goes wrong
      setShowSaveErrorModal(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En Proceso';
      case 'reviewed': return 'Revisado';
      default: return status;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  // Get unique categories for filter dropdown
  const categories = ['all', ...Array.from(new Set(editableItems.map(item => item.category)))];

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Cargando inventario...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700">{error}</h2>
        </div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Inventario no encontrado</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/inventory')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Cerrar
        </Button>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">
              Toma de Inventario - {audit.locationName}
            </h1>
            {audit.isRecount && (
              <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-lg font-semibold">
                🔄 Re-conteo
              </span>
            )}
          </div>
          <div className="flex items-center justify-between w-full mt-2">
            <div>
              <span className="text-sm font-medium text-gray-600">Fecha de Auditoría: </span>
              <span className="text-sm font-semibold">{formatDate(audit.auditDate)}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Auditor: </span>
              <span className="text-sm font-semibold">{audit.auditorName}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Estado: </span>
              <Badge className={`${getStatusColor(audit.status)} text-xs px-2 py-0.5`}>
                {getStatusText(audit.status)}
              </Badge>
            </div>
          </div>
        </div>

      </div>

      {/* Filters Card */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="category-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Categoría:
              </Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat === 'all' ? 'Todas' : cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Review filter for completed and reviewed status */}
            {(audit?.status === 'completed' || audit?.status === 'reviewed') && (
              <div className="flex items-center gap-2">
                <Label htmlFor="review-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Estado:
                </Label>
                <Select value={reviewFilter} onValueChange={setReviewFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="reviewed">Revisados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Filtro de discrepancias - Solo mostrar cuando no esté en proceso */}
            {audit?.status !== 'in_progress' && (
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium text-gray-700">Mostrar:</Label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="show-all-items"
                      name="item-filter"
                      checked={!showOnlyDiscrepancies}
                      onChange={() => setShowOnlyDiscrepancies(false)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <Label htmlFor="show-all-items" className="text-sm font-medium text-gray-700">
                      Todos los productos
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="show-discrepancies-only"
                      name="item-filter"
                      checked={showOnlyDiscrepancies}
                      onChange={() => setShowOnlyDiscrepancies(true)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                    />
                    <Label htmlFor="show-discrepancies-only" className="text-sm font-medium text-gray-700">
                      Solo discrepancias
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Items del Inventario</span>
            <span className="text-sm font-normal text-gray-600">
              {editableItems.length} productos
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <InventoryAuditItemsTable
            items={editableItems}
            auditStatus={audit?.status || 'in_progress'}
            showOnlyDiscrepancies={showOnlyDiscrepancies}
            categoryFilter={categoryFilter}
            reviewFilter={reviewFilter}
            isCajero={user?.positionId === 3}
            onQuantityChange={updateActualQuantity}
            onReviewToggle={updateItemReviewStatus}
            onConfirmQuantityChange={updateConfirmedQuantity}
            onDiscrepancyReasonChange={updateDiscrepancyReason}
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div></div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/inventory')}
          >
            Cerrar
          </Button>
          {audit.status === 'in_progress' && !(audit.isRecount && user?.positionId !== 3) && (
            <>
              <Button
                onClick={() => startReview()}
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Guardando...' : 'Guardar Conteos'}
              </Button>
              <Button
                onClick={completeInventory}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Finalizar Inventario
              </Button>
            </>
          )}
          {audit.status === 'completed' && (
            <>
              <Button
                onClick={() => startReview()}
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Guardando...' : 'Guardar Revisión'}
              </Button>
              <Button
                onClick={() => setShowFinalizeConfirmModal(true)}
                disabled={isSaving || !editableItems.some(item => item.isReviewed)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {isSaving ? 'Finalizando...' : 'Finalizar Revisión'}
              </Button>
            </>
          )}
          {audit.status === 'reviewed' && !audit.hasBeenRecounted && (() => {
            const itemsWithDiscrepanciesNotReviewed = editableItems.filter(
              item => item.discrepancy !== 0 && !item.isReviewed
            );

            // Si ya existe un reconteo, mostrar enlace para verlo
            if (recountStatus.hasRecount && recountStatus.recountAuditId) {
              return (
                <Button
                  onClick={() => navigate(`/inventory/audit/${recountStatus.recountAuditId}`)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Ver Reconteo
                </Button>
              );
            }

            // Si no existe reconteo, mostrar botón normal
            return itemsWithDiscrepanciesNotReviewed.length > 0 && (
              <Button
                onClick={handleSendToRecount}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Package className="w-4 h-4 mr-2" />
                Enviar items a contar nuevamente ({itemsWithDiscrepanciesNotReviewed.length})
              </Button>
            );
          })()}
        </div>
      </div>

      {/* Mini-Inventario Modal - TEMPORALMENTE OCULTO PARA TODOS LOS ROLES */}
      {/* TEMPORALMENTE OCULTO - Descomentar para reactivar */}
      {false && showMiniInventory && audit && auditId && (
        <MiniInventoryModal
          isOpen={showMiniInventory}
          auditId={auditId}
          locationId={audit.locationId}
          performedBy={audit.auditorName}
          onComplete={handleMiniInventoryComplete}
          onCancel={handleMiniInventoryCancel}
        />
      )}

      {/* Modal de Confirmación para Re-conteo */}
      <Dialog open={showRecountModal} onOpenChange={setShowRecountModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
              Enviar Items a Re-conteo
            </DialogTitle>
            <DialogDescription className="text-base">
              Esta acción no se puede revertir. Se procederá a crear un nuevo inventario.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {(() => {
              const itemsToRecount = editableItems.filter(item => item.discrepancy !== 0 && !item.isReviewed);
              return (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <RotateCcw className="h-5 w-5 text-orange-600" />
                    <h3 className="font-medium text-orange-900">
                      {itemsToRecount.length} productos serán enviados para re-conteo
                    </h3>
                  </div>
                  <p className="text-sm text-orange-700">
                    Esta acción no se puede revertir. Se procederá a crear un nuevo inventario.
                  </p>
                  <p className="text-sm text-orange-700 mt-2">
                    Solo los productos con discrepancias que no han sido revisados requerirán nuevo conteo.
                    Los productos sin discrepancias mantendrán sus valores actuales.
                  </p>
                </div>
              );
            })()}
          </div>

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={cancelRecount}>
              Cancelar
            </Button>
            <Button
              onClick={confirmRecount}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Confirmar Re-conteo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmación de Revisión */}
      <Dialog open={showReviewConfirmModal} onOpenChange={setShowReviewConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <CheckCircle className="h-5 w-5" />
              Confirmar Revisión
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              ¿Estás seguro de revisar los items ({itemsToReview})?
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Esta acción marcará estos items como revisados.
            </p>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowReviewConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmReview}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Confirmar Revisión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Éxito de Revisión */}
      <Dialog open={showReviewSuccessModal} onOpenChange={setShowReviewSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Revisión Completada
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              Revisión de items completada exitosamente.
            </p>
            <p className="text-gray-800 font-semibold mt-2">
              {reviewedItemsCount} productos revisados.
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Puedes continuar revisando más items o finalizar el inventario cuando hayas terminado.
            </p>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowReviewSuccessModal(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmación de Finalización */}
      <Dialog open={showFinalizeConfirmModal} onOpenChange={setShowFinalizeConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <CheckCircle className="h-5 w-5" />
              Finalizar Revisión
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-3">
            <p className="text-gray-600">
              ¿Estás seguro de finalizar la revisión del inventario?
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-purple-800">
                {editableItems.filter(item => item.isReviewed).length} de {editableItems.length} productos revisados
              </p>
              {editableItems.filter(item => !item.isReviewed).length > 0 && (
                <p className="text-purple-600 mt-1">
                  {editableItems.filter(item => !item.isReviewed).length} productos sin revisar
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFinalizeConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmFinalizeInventory}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Éxito de Finalización */}
      <Dialog open={showFinalizeSuccessModal} onOpenChange={setShowFinalizeSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Inventario Finalizado
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              {finalizeSuccessMessage}
            </p>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowFinalizeSuccessModal(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Error de Finalización */}
      <Dialog open={showFinalizeErrorModal} onOpenChange={setShowFinalizeErrorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Error al Finalizar
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              Error al finalizar la revisión del inventario. Inténtalo nuevamente.
            </p>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowFinalizeErrorModal(false)}
              className="bg-red-600 hover:bg-red-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Éxito de Re-conteo */}
      <Dialog open={showRecountSuccessModal} onOpenChange={setShowRecountSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              ¡Nuevo inventario de re-conteo creado exitosamente!
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              {recountedItemsCount} productos enviados a re-conteo.
            </p>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowRecountSuccessModal(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: No Items to Review */}
      <Dialog open={showNoItemsToReviewModal} onOpenChange={setShowNoItemsToReviewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Selección Requerida
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              No hay items marcados para revisar.
            </p>
            <p className="text-gray-600 mt-2">
              Por favor, marca los items que deseas revisar usando los checkboxes de la columna "REVISADO".
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowNoItemsToReviewModal(false)}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: No Items to Recount */}
      <Dialog open={showNoItemsToRecountModal} onOpenChange={setShowNoItemsToRecountModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Package className="h-5 w-5" />
              Sin Items para Reconteo
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              No hay items que requieran reconteo.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowNoItemsToRecountModal(false)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Recount Error */}
      <Dialog open={showRecountErrorModal} onOpenChange={setShowRecountErrorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Error de Reconteo
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Error al crear el inventario de re-conteo. Inténtalo nuevamente.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowRecountErrorModal(false)}
              className="bg-red-600 hover:bg-red-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Save Error */}
      <Dialog open={showSaveErrorModal} onOpenChange={setShowSaveErrorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Error al Guardar
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Error al guardar los conteos. Inténtalo nuevamente.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowSaveErrorModal(false)}
              className="bg-red-600 hover:bg-red-700"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Mini Inventory Complete */}
      <Dialog open={showMiniCompleteModal} onOpenChange={setShowMiniCompleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              ¡Mini-inventario Completado!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Mini-inventario completado exitosamente! Ahora puedes continuar trabajando con el inventario.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowMiniCompleteModal(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Mini Inventory Cancel */}
      <Dialog open={showMiniCancelModal} onOpenChange={setShowMiniCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Mini-inventario Requerido
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Debes completar el mini-inventario para continuar con el inventario en proceso.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowMiniCancelModal(false)}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Incomplete Items */}
      <Dialog open={showIncompleteItemsModal} onOpenChange={setShowIncompleteItemsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Inventario Incompleto
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Aún faltan <span className="font-semibold">{incompleteItemsCount}</span> productos por contar.
            </p>
            <p className="text-gray-600 mt-2">
              Por favor completa todos los conteos antes de finalizar el inventario.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowIncompleteItemsModal(false)}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Continuar Conteo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Save Success */}
      <Dialog open={showSaveSuccessModal} onOpenChange={setShowSaveSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              ¡Conteos Guardados!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Conteos guardados exitosamente.
            </p>
            <p className="text-gray-600 mt-2">
              Puedes continuar con el inventario o finalizar cuando hayas terminado.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowSaveSuccessModal(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Final Success */}
      <Dialog open={showFinalSuccessModal} onOpenChange={setShowFinalSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              ¡Inventario Completado Exitosamente!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-3">
              <div className="bg-green-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">Productos auditados:</span>
                    <span className="text-green-700 font-semibold">{finalSuccessData.totalItems}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Discrepancias encontradas:</span>
                    <span className={`font-semibold ${finalSuccessData.discrepancies > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {finalSuccessData.discrepancies}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Valor total:</span>
                    <span className="text-green-700 font-semibold">
                      S/ {finalSuccessData.totalValue.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-medium">Estado:</span>
                    <span className="text-green-600 font-semibold">Completado</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-center">
                El inventario ha sido marcado como completado y está listo para revisión.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowFinalSuccessModal(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Perfecto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Inventory Completion Confirmation */}
      <Dialog open={showDiscrepancyConfirmModal} onOpenChange={setShowDiscrepancyConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <CheckCircle className="h-5 w-5" />
              Finalizar Inventario
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              ¿Confirmar finalización del inventario?
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Las discrepancias serán registradas y podrán ser revisadas posteriormente.
            </p>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDiscrepancyConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDiscrepanciesAndFinalize}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmar Finalización
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};